/**
 * WebSocket provider — owns the org's ONE activity socket (PX-W6.1, ported
 * from xelerate-ai's `providers/WebSocketProvider.tsx`; protocol v2 since
 * TASK-7922504E).
 *
 * One socket per active org (`/api/v1/ws/:orgId?token=…`), fed the reactive
 * access token, with every event frame routed through {@link dispatchActivity}
 * (the declarative `EVENT_TYPE_MAP`) exactly once. Components add their own
 * contextIds through `useActivityContext` / `<ActivityTracker>`.
 *
 * What the provider itself holds depends on the session's side (AC-34):
 *  - a firm or platform session holds `org:<orgId>` — the org firehose every
 *    list surface's realtime refresh rides on;
 *  - a CLIENT-side session never holds `org:<orgId>` (the server refuses it
 *    anyway, AC-32): its project surfaces hold `project:<id>` themselves;
 *  - every session holds `user:<ownId>`, so a client-side user still receives
 *    its own notifications (the server grants `user:` only for oneself).
 *
 * Both inputs are reactive, so the connection arms itself whenever the session
 * lands (sign-in, silent refresh on boot) and tears down on sign-out or org
 * change with no listener wiring:
 *  - `activeOrgId` / role / principal from the session slice;
 *  - the access token via `useSyncExternalStore` on the in-memory token store
 *    — the token can arrive a moment AFTER the org id on a cold load.
 */

import { useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react'
import { useStore } from 'react-redux'

import { isClientSideRole } from '@/access/roles'
import {
  selectActiveOrgId,
  selectRoleKey,
  selectSessionPrincipalKey
} from '@/features/auth/slices/sessionSlice'
import {
  getAccessToken,
  subscribeAccessToken
} from '@/features/auth/services/authService.http'
import {
  createActivitySocket,
  dispatchActivity,
  type ActivitySocket,
  type ConnectionStatus
} from '@/services/websocket'
import { useAppDispatch, useAppSelector, type RootState } from '@/store'
import { WebSocketContext, type WebSocketContextValue } from './webSocketContext'

interface WebSocketProviderProps {
  children: ReactNode
}

const NO_IDS: readonly string[] = []
const noopUnsubscribe = (): void => {}
const subscribeNothing = (): (() => void) => noopUnsubscribe
const disconnected = (): ConnectionStatus => 'disconnected'

/** Opens the org's activity socket when an org id and token exist. */
export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const orgId = useAppSelector(selectActiveOrgId)
  const roleKey = useAppSelector(selectRoleKey)
  const principalKey = useAppSelector(selectSessionPrincipalKey)
  const token = useSyncExternalStore(subscribeAccessToken, getAccessToken, getAccessToken)

  // Creating the socket connects nothing: `start()` + a token do, below.
  const socket = useMemo<ActivitySocket | null>(
    () => (orgId === null ? null : createActivitySocket({ orgId })),
    [orgId]
  )

  useEffect(() => socket?.start(), [socket])

  useEffect(() => {
    socket?.setToken(token)
  }, [socket, token])

  useEffect(
    () => socket?.onFrame((event) => dispatchActivity(event, dispatch, store.getState)),
    [socket, dispatch, store]
  )

  // Fail closed: no derived role yet means no org firehose.
  const holdsOrg = roleKey !== null && !isClientSideRole(roleKey)
  const heldIds = useMemo<readonly string[]>(() => {
    if (orgId === null) return NO_IDS
    const ids: string[] = []
    if (holdsOrg) ids.push(`org:${orgId}`)
    if (principalKey !== null) ids.push(`user:${principalKey}`)
    return ids
  }, [orgId, holdsOrg, principalKey])

  useEffect(() => {
    if (socket === null || heldIds.length === 0) return
    return socket.retain(heldIds)
  }, [socket, heldIds])

  const status = useSyncExternalStore(
    socket === null ? subscribeNothing : socket.onConnectionStatus,
    socket === null ? disconnected : socket.getConnectionStatus,
    socket === null ? disconnected : socket.getConnectionStatus
  )

  const value = useMemo<WebSocketContextValue>(
    () => ({ isConnected: status === 'connected', status, socket }),
    [status, socket]
  )

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>
}

export default WebSocketProvider
